import type { Doc } from "building-types/doc";
import type { DocOption, DocOptionSet } from "building-types/options/optionSet";
import { SettingsType } from "building-types/settings/settingsType";
import type { RWTable, Table } from "common/data/table";
import { SalesCategory } from "common/types/salesCategoryTypesEnum";
import { deleteIn, setIn } from "common/utils/objectUtils";
import { maxSortIndexFromTable } from "common/utils/sortIndex";
import { optionSetDefaultOptionId } from "./options/optionSetBaseUtils";
import { mergeOverridesIntoSystemOptionSet } from "./options/optionTemplate";
import {
  DEFAULT_EXTERIOR_PALETTE_TAGS,
  DEFAULT_INTERIOR_PALETTE_NAME,
  DEFAULT_INTERIOR_PALETTE_TAGS,
  DEFAULT_EXTERIOR_PALETTE_NAME,
  PALETTE_OPTION_SET_SUFFIX,
} from "./options/paletteOptionSet";
import { SETTINGS_OPTION_SET_SUFFIX, settingsOptionSetId } from "./options/settingsOptionSet";
import { builtinHomeSettingsPrettyName } from "./settings/builtinHomeSettingsId";
import interiorPaletteLibrary from "./settings/interiorMaterialSettingsLibrary";

export function docSyncSettingsOptionSets(doc: Doc): Doc {
  // persist previous palette overrides for setting a user-set prettyName and/or price
  const paletteOptionSets: RWTable<DocOptionSet> = {};

  for (const key of Object.keys(doc.optionSets)) {
    if (key.endsWith(PALETTE_OPTION_SET_SUFFIX) || key.endsWith(SETTINGS_OPTION_SET_SUFFIX)) {
      const paletteOptionSet = doc.optionSets[key];
      paletteOptionSets[key] = paletteOptionSet;

      doc = deleteIn(doc, ["optionSets", key]);
    }
  }

  doc = docSyncExteriorMaterialOptionSets(doc, paletteOptionSets);
  doc = docSyncInteriorMaterialOptionSets(doc, paletteOptionSets);
  return doc;
}

function docSyncExteriorMaterialOptionSets(doc: Doc, optionSets: Doc["optionSets"]): Doc {
  for (const settingsId in doc.settings) {
    const settings = doc.settings[settingsId];
    if (settings.type !== SettingsType.EXTERIOR_MATERIAL_SETTINGS) {
      continue;
    }

    const palettes = settings.palettes;
    if (!palettes || palettes.length === 0) {
      continue;
    }

    if (!(palettes.length > 0)) {
      console.assert(false);
    }

    const optionSetId = settingsOptionSetId(settingsId, "palettes");
    const options = paletteOptionsNew(palettes, defaultPalletNames(doc, palettes, "exterior"), optionSets, optionSetId);

    let defaultValue = optionSetDefaultOptionId(optionSets[optionSetId], optionSets);
    if (defaultValue === undefined || defaultValue === null || options[defaultValue] === undefined) {
      defaultValue = palettes[0];
    }

    let param: DocOptionSet = {
      id: optionSetId,
      entityType: "OPTION_SET",
      sortIndex: maxSortIndexFromTable(doc.optionSets) + 1,
      defaultValue,
      name: settings.paramName ?? DEFAULT_EXTERIOR_PALETTE_NAME,
      options,
      salesCategory: SalesCategory.DESIGN_PALETTES,
      tags: DEFAULT_EXTERIOR_PALETTE_TAGS,
      optionSetType: "SYSTEM",
      disabled: optionSets[optionSetId]?.disabled,
      builderOptionSetCode: optionSets[optionSetId]?.builderOptionSetCode,
    };

    param = mergeOverridesIntoSystemOptionSet(palettes, param, doc.optionSets, doc.optionSets[optionSetId]);

    doc = setIn(doc, ["optionSets", optionSetId], param);
  }

  return doc;
}

function docSyncInteriorMaterialOptionSets(doc: Doc, optionSets: Doc["optionSets"]): Doc {
  for (const settingsId in doc.settings) {
    const settings = doc.settings[settingsId];
    if (settings.type !== SettingsType.INTERIOR_MATERIAL_SETTINGS) {
      continue;
    }

    const palettes = settings.palettes;
    if (!palettes || palettes.length === 0) {
      continue;
    }

    const optionSetId = settingsOptionSetId(settingsId, "palettes");
    const options = paletteOptionsNew(palettes, defaultPalletNames(doc, palettes, "interior"), optionSets, optionSetId);

    let defaultValue = optionSetDefaultOptionId(optionSets[optionSetId], optionSets);
    if (defaultValue === undefined || defaultValue === null || options[defaultValue] === undefined) {
      defaultValue = palettes[0];
    }

    let param: DocOptionSet = {
      id: optionSetId,
      entityType: "OPTION_SET",
      sortIndex: 0,
      defaultValue,
      name: settings.paramName ?? DEFAULT_INTERIOR_PALETTE_NAME,
      options,
      salesCategory: SalesCategory.OPERATIONS,
      tags: DEFAULT_INTERIOR_PALETTE_TAGS,
      optionSetType: "SYSTEM",
      disabled: optionSets[optionSetId]?.disabled,
      builderOptionSetCode: optionSets[optionSetId]?.builderOptionSetCode,
    };

    param = mergeOverridesIntoSystemOptionSet(palettes, param, doc.optionSets, doc.optionSets[optionSetId]);

    doc = setIn(doc, ["optionSets", optionSetId], param);
  }

  return doc;
}

function defaultPalletNames(doc: Doc, paletteIds: string[], type: "interior" | "exterior"): Table<string> {
  const palletNames: RWTable<string> = {};
  for (const paletteId of paletteIds) {
    const docName = doc.palettes[paletteId]?.name;
    if (docName) {
      palletNames[paletteId] = docName;
    } else if (type === "interior") {
      palletNames[paletteId] = interiorPaletteLibrary[paletteId]?.name;
    } else {
      palletNames[paletteId] = builtinHomeSettingsPrettyName[paletteId];
    }
  }
  return palletNames;
}

/**
 * @param paletteIds - An array of palette ids from doc.settings
 * @param palletNames - A map of palette ids to their default names
 * @param paletteOptionSets - Palette option sets that previously existed in the doc
 * @param optionSetId - The id of the option set to create
 */
export function paletteOptionsNew(
  paletteIds: string[],
  palletNames: Table<string>,
  paletteOptionSets: Doc["optionSets"],
  optionSetId: string,
) {
  const options: RWTable<DocOption> = {};

  const existingOptionsByPaletteId = Object.fromEntries(
    Object.values(paletteOptionSets).flatMap((os) => Object.entries(os.options ?? {})),
  );

  for (const paletteId of paletteIds) {
    const palletName = palletNames[paletteId];

    // This preserves pallets moving to a new option set id
    const existingOption = existingOptionsByPaletteId[paletteId];

    if (existingOption) {
      options[paletteId] = {
        ...existingOption,
        parentId: optionSetId,
        name: existingOption.name || palletName,
      };
    } else {
      options[paletteId] = {
        id: paletteId,
        parentId: optionSetId,
        sortIndex: Object.values(options).length,
        name: palletName,
        entityType: "OPTION",
      };
    }
  }

  return options;
}
